import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../../../../services/firebase";
import { usePagination } from "../../../../hooks/usePagination";
import { useTenant } from "../../../../hooks/useTenant";
import { roleToFirestore } from "../../../../models/role";
import { toast } from "react-toastify";
import { ConfirmDialog } from "../../../../components/Common";
import { getDefaultRoles } from "../constants";
import RoleService from "../../../../services/role_service";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "../../../../services/audit_service";
import { COLLECTIONS } from "../../../../services/collections";

/**
 * useRoles
 * Gancho de lógica para o módulo de Cargos (Roles).
 * Utiliza o RoleService para persistência.
 */
export const useRoles = () => {
    const { idTenant, user } = useTenant();
    const [isSaving, setIsSaving] = useState(false);

    const paginationOptions = useMemo(() => ({
        pageSize: 50,
        orderField: "name",
        orderDirection: "asc"
    }), []);

    // Paginação dos Cargos (Roles)
    const { data: roles, loading, error, refresh } = usePagination(
        `tenants/${idTenant}/roles`,
        idTenant,
        paginationOptions
    );

    /**
     * Auto-Seed: Cria os cargos padrões APENAS UMA VEZ se o tenant não tiver nenhum.
     */
    const hasSeeded = useRef(false);

    useEffect(() => {
        if (hasSeeded.current) return;
        if (loading || error || !idTenant || isSaving) return;
        if (roles.length > 0) {
            hasSeeded.current = true;
            return;
        }

        hasSeeded.current = true;

        const seedDefaultRoles = async () => {
            try {
                const defaultRoles = getDefaultRoles();

                const promises = defaultRoles.map((role) => {
                    const roleData = roleToFirestore(role, idTenant, user);
                    return addDoc(collection(db, "tenants", idTenant, COLLECTIONS.ROLES), roleData);
                });

                await Promise.all(promises);

                AuditService.log({
                    idTenant,
                    user,
                    module: AUDIT_MODULES.ROLES,
                    action: AUDIT_ACTIONS.CREATE,
                    description: `Criou automaticamente os ${defaultRoles.length} cargos padrões para a unidade.`
                });

                toast.success("Cargos padrões configurados com sucesso!");
                refresh();
            } catch (err) {
                console.error("Erro ao injetar os cargos padrões:", err);
                toast.error("Erro ao configurar cargos padrões.");
                hasSeeded.current = false;
            }
        };

        seedDefaultRoles();
    }, [loading, error, idTenant, roles.length, isSaving, refresh, user]);

    /**
     * Salvar um Cargo
     */
    const saveRole = useCallback(async (formData) => {
        if (!idTenant) return;
        setIsSaving(true);

        try {
            await RoleService.save(formData, idTenant, user, roles);
            toast.success(formData.id ? "Cargo atualizado!" : "Novo cargo cadastrado!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao salvar cargo:", err);
            toast.error("Erro ao salvar cargo.");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user, roles, refresh]);

    /**
     * Excluir um Cargo
     */
    const deleteRole = useCallback(async (roleId) => {
        if (!idTenant) return;

        const roleToDelete = roles.find(r => r.id === roleId);

        const confirmed = await ConfirmDialog({
            title: "Excluir Cargo?",
            text: `Tem certeza que deseja excluir o cargo "${roleToDelete?.name || 'este cargo'}"? Esta ação pode impactar os funcionários vinculados.`,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar"
        });

        if (!confirmed) return;

        try {
            await RoleService.delete(roleId, idTenant, user, roleToDelete?.name);
            toast.success("Cargo removido!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao excluir cargo:", err);
            toast.error("Não foi possível excluir.");
            return false;
        }
    }, [idTenant, user, roles, refresh]);

    return {
        roles,
        loading,
        error,
        isSaving,
        actions: {
            saveRole,
            deleteRole,
            refresh
        }
    };
};
